import { EZChatControllerNew } from "@axl/ez-chat-for-school";
import {
  mountSchoolChat,
  type SchoolChatHandle,
} from "@axl/site-chat/src/school/mountSchoolChat";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { appSettings } from "@app/shared/lib/app-settings";
import { useSchool, useTokenStorage } from "@app/shared/lib/hooks";

/**
 * `pending` — asking AccelChat whether the school has a membership chat; `site` — it has: the website chat
 * (`@axl/site-chat` in its School mode) replaces the old one; `legacy` — it has not (none, disabled or not marked
 * for the membership area): the old support chat, exactly as before.
 */
type ChatKind = "pending" | "site" | "legacy";

type ChatContextType = {
  chatController: EZChatControllerNew | null;
  setChatController: (controller: EZChatControllerNew | null) => void;
  isChatInited: boolean;
  unreadCount: number;
  kind: ChatKind;
};

const ChatContext = createContext<ChatContextType>({} as ChatContextType);

/** AccelChat's root: School's chat API setting ends with `/api`, the website chat adds its own paths. */
const chatRoot = () =>
  String(appSettings.chat.apiUrl ?? "").replace(/\/api\/?$/, "");

/**
 * The website chat's handle, dressed as the old controller: the header button and the "technical support" links
 * call `open()` / `close()` / `visible` and need not know which chat is behind them.
 */
const asController = (chat: SchoolChatHandle) =>
  ({
    open: () => chat.open(),
    close: () => chat.close(),
    get visible() {
      return chat.isOpen;
    },
  }) as unknown as EZChatControllerNew;

export const ChatProvider: FC<PropsWithChildren> = ({ children }) => {
  const tokenStorage = useTokenStorage();
  const tokenRef = useRef<string | undefined>(undefined);
  tokenRef.current = tokenStorage.accessToken;
  const signedIn = !!tokenStorage.accessToken;

  const [kind, setKind] = useState<ChatKind>("pending");
  const [siteController, setSiteController] =
    useState<EZChatControllerNew | null>(null);
  const [legacyController, setChatController] =
    useState<EZChatControllerNew | null>(null);
  const [isChatInited, setChatInited] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!signedIn) {
      setKind("pending");
      return;
    }
    const chat = mountSchoolChat({
      apiUrl: chatRoot(),
      getToken: () => tokenRef.current,
    });
    let alive = true;
    const unsubscribe = chat.subscribe(() => setUnreadCount(chat.unread));
    chat.ready
      .then((ok) => {
        if (!alive) return;
        if (!ok) {
          setKind("legacy");
          return;
        }
        setSiteController(asController(chat));
        setChatInited(true);
        setUnreadCount(chat.unread);
        setKind("site");
      })
      .catch(() => alive && setKind("legacy"));
    return () => {
      alive = false;
      unsubscribe();
      chat.dispose();
      setSiteController(null);
    };
  }, [signedIn]);

  useEffect(() => {
    if (kind !== "legacy" || !legacyController) return;
    const handler = () => setChatInited(true);
    (legacyController as any).onInited.on(handler);
    return () => (legacyController as any).onInited.off(handler);
  }, [kind, legacyController]);

  useEffect(() => {
    if (kind !== "legacy" || !legacyController || !isChatInited) return;
    const handler = (count: number) => setUnreadCount(count);
    legacyController.onUnreadCountChanged.on(handler);
    return () => legacyController.onUnreadCountChanged.off(handler);
  }, [kind, legacyController, isChatInited]);

  const ctxValue = {
    chatController: kind === "site" ? siteController : legacyController,
    setChatController,
    isChatInited,
    unreadCount,
    kind,
  };

  return (
    <ChatContext.Provider value={ctxValue}>{children}</ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);

/**
 * Whether the student has a chat at all: the membership chat when the school has one, otherwise the old support
 * chat under its own switch (`supportChatEnabled`).
 */
export const useChatAvailable = () => {
  const school = useSchool();
  const { kind } = useChat();
  return kind === "site" || (kind === "legacy" && school.supportChatEnabled);
};
